<template>
	<div class="wrapper" :style="globalPaletteVars">
		<UiDialog v-if="idleStore.isModalVisible">
			<template #default>
				<UiElement :settings="dialog.appearance.body.settings">
					<div class="d-flex flex-column align-items-center justify-content-center">
						<UiText :settings="heading1" class="text-center">
							{{ translations?.continueShopping || '{continueShopping}' }}
						</UiText>

						<UiButton :settings="button1" @click="idleStore.continueUsage">
							{{ translations?.proceedOrder || '{proceedOrder}' }}
						</UiButton>

						<div class="py-4"/>

						<UiButton :settings="button2" @click="cancelOrder">
							{{ translations?.cancelOrder || '{cancelOrder}' }}
						</UiButton>
					</div>
				</UiElement>
			</template>
		</UiDialog>

		<router-view v-slot="{ Component }">
			<transition :name="transitionName">
				<component :is="Component" :key="$route.path" />
			</transition>
		</router-view>

		<div class="d-none" style="position: fixed; left: 0; top: 0; background: #fcfcfc">
			<p class="mb-0">Kiosk ID: {{ kioskId }}</p>

			<p class="mb-0">Account ID: {{ accountId }}</p>

			<p class="mb-0">Branch ID: {{ branchId }}</p>
		</div>
	</div>
</template>

<script setup>
	import { onMounted, computed, onUnmounted } from 'vue';
	import { navigationDirection } from './router';
	import { useKioskStore } from '@/stores/useKioskStore';
	import { useThemeStore } from '@/stores/useThemeStore';
	import { useOrderStore } from '@/stores/useOrderStore';
	import { useIdleStore } from '@/stores/useIdleStore';
	import { useTranslationsStore } from '@/stores/useTranslationsStore';
	import { storeToRefs } from 'pinia';
	import { useInheritedSettings } from '@/composables/useInheritedSettings';
	import UiDialog from '@/components/UI/UiDialog.vue';
	import UiButton from '@/components/UI/UiButton.vue';
	import UiElement from '@/components/UI/UiElement.vue';
	import UiText from '@/components/UI/UiText.vue';

	const orderStore = useOrderStore();
	const { kioskId, accountId, branchId } = useKioskStore();
	const { addProduct } = orderStore;

	const themeStore = useThemeStore();
	const translationsStore = useTranslationsStore();
	const idleStore = useIdleStore();

	const { theme } = storeToRefs(themeStore)
	const { cart } = storeToRefs(orderStore);
	const { translations } = storeToRefs(translationsStore);

	const transitionName = computed(() => {
		return navigationDirection.value === 'forward' ? 'slide-forward' : 'slide-backward';
	});


	const globalPaletteVars = computed(() => {
		if (!theme.value) return {};

		const { palette } = theme.value.general;

		return Object.fromEntries(
			Object.entries(palette).map(([key, value]) => [`--palette-${key}`, value])
		);
	});

	const dialog = computed(() => theme.value.pages.dialogKioskTimeout);
	const heading1 = dialog.value ? useInheritedSettings(computed(() => dialog.value.elements?.heading1)) : null;
	const button1 = dialog.value ? useInheritedSettings(computed(() => dialog.value.elements?.button1)) : null;
	const button2 = dialog.value ? useInheritedSettings(computed(() => dialog.value.elements?.button2)) : null;

	const resetOnActivity = () => idleStore.resetTimer();

	function cancelOrder() {
		idleStore.cancelUsage();
		orderStore.clearCart();
	}

	onMounted(async () => {
		const BACKOFFICE = process.env.VUE_APP_BACKOFFICE;

		window.addEventListener('message', (event) => {
			if (window.self !== window.top) {
				themeStore.applyExternalTheme(event);

				if (cart.value.length === 0) {
					addProduct({ label: 'Test Product 1', id: 0, price: 100 });
					addProduct({ label: 'Test Product 2', id: 1, price: 49 });
					addProduct({ label: 'Test Product 2', id: 1, price: 49 });
				}
			} else {
				const events = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart'];
				events.forEach(event => window.addEventListener(event, resetOnActivity));

				idleStore.startTimer();
			}
		});
		if (window.self !== window.top) {
			window.parent.postMessage('ready', BACKOFFICE);
		}
	});

	onUnmounted(() => {
		const events = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart'];
		events.forEach(event => window.removeEventListener(event, resetOnActivity));
		idleStore.clearTimers();
	});
</script>
